import React, { useEffect, useState } from 'react';
import { conversationService } from '../../../services/conversationService';
import { ConversationActivity, ConversationThreadItem } from '../../../types/conversationActivity';
import {
  CONVERSATION_ACTIVITY_LABELS,
  CONVERSATION_STATUS_LABELS,
} from '../../../types/atendimentoEnums';
import { formatConversationActivityDetail } from '../../../utils/conversationActivityDisplay';
import { WorkspaceThemeClasses } from './workspaceTheme';

interface WorkspaceHistoryPanelProps {
  theme: WorkspaceThemeClasses;
  conversationId: string | null;
  isVisible: boolean;
  onClose: () => void;
  onSelectConversation: (id: string) => void;
}

export const WorkspaceHistoryPanel: React.FC<WorkspaceHistoryPanelProps> = ({
  theme,
  conversationId,
  isVisible,
  onClose,
  onSelectConversation,
}) => {
  const [activities, setActivities] = useState<ConversationActivity[]>([]);
  const [thread, setThread] = useState<ConversationThreadItem[]>([]);
  const [showThread, setShowThread] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isVisible || !conversationId)
    {
      return;
    }

    const loadHistory = async () => {
      setLoading(true);
      try
      {
        const [activitiesData, threadData] = await Promise.all([
          conversationService.getActivities(conversationId),
          conversationService.getThread(conversationId),
        ]);
        setActivities(activitiesData);
        setThread(threadData);
      }
      catch (error)
      {
        console.error('Erro ao carregar histórico:', error);
      }
      finally
      {
        setLoading(false);
      }
    };

    void loadHistory();
  }, [conversationId, isVisible]);

  if (!isVisible)
  {
    return null;
  }

  return (
    <div className={`absolute right-0 top-full mt-1 w-80 max-w-[calc(100vw-1.5rem)] rounded-lg border z-20 ${theme.surface} ${theme.border} flex flex-col max-h-[28rem]`}>
      <div className={`px-4 py-3 border-b flex items-center justify-between ${theme.border}`}>
        <h3 className={`font-semibold text-sm ${theme.text}`}>Auditoria</h3>
        <button
          type="button"
          onClick={onClose}
          className={`rounded-md p-1 ${theme.muted}`}
          aria-label="Fechar"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3">
              {activities.length === 0 ? (
                <p className={`text-sm text-center py-4 ${theme.muted}`}>Nenhum evento registrado.</p>
              ) : (
                activities.map((activity) => {
                  const detail = formatConversationActivityDetail(activity);

                  return (
                  <div key={activity.id} className="text-sm border-l-2 border-blue-200 pl-3">
                    <p className={`font-medium ${theme.text}`}>
                      {CONVERSATION_ACTIVITY_LABELS[activity.activityType]}
                    </p>
                    <p className={`text-xs ${theme.muted}`}>
                      {new Date(activity.createdAt).toLocaleString('pt-BR')}
                      {activity.changedByUserName ? ` · ${activity.changedByUserName}` : ''}
                    </p>
                    {detail && (
                      <p className={`text-xs mt-1 ${theme.muted}`}>{detail}</p>
                    )}
                  </div>
                  );
                })
              )}
            </div>

            {thread.length > 1 && (
              <div className={`border-t pt-3 ${theme.border}`}>
                <button
                  type="button"
                  onClick={() => setShowThread((prev) => !prev)}
                  className={`w-full text-left text-sm font-semibold ${theme.text}`}
                >
                  Histórico encadeado ({thread.length})
                </button>
                {showThread && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {thread.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          onSelectConversation(item.id);
                          onClose();
                        }}
                        className={`block w-full text-left text-sm rounded-md px-2 py-1 ${
                          item.id === conversationId
                            ? theme.accentSoft
                            : theme.muted
                        }`}
                      >
                        #{item.displayId}
                        {' · '}
                        {CONVERSATION_STATUS_LABELS[item.conversation.status]}
                        {' · '}
                        {item.messages.length} msg
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
